import type { Theme } from '@mui/material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid2 as Grid,
  type SvgIconTypeMap,
  Tooltip,
  Typography,
} from '@mui/material';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { Fragment, useState } from 'react';
import type { ComponentDefinition } from '@/admins/SmartReports/components/Components/types';
import { COMPONENT_TYPES_ICONS } from '@/admins/SmartReports/constants';
import { getThumbnailComponent } from '@/admins/SmartReports/helpers';
import { useGetComponents } from '@/admins/SmartReports/hooks/useGetComponents';
import {
  SmartReportComponentTypesEnum,
  type SmartReportTypeEnum,
} from '@/admins/SmartReports/types';
import { DragContainer } from '@/components/DragAndDrop/DragContainer';
import { SmartReportLayouts } from '../Layouts';
import type { LayoutDefinition } from '../Layouts/types';
import { useEditSmartReport } from './EditSmartReportContext';

const styles = {
  number: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'Barlow',
  },
  selected: {
    border: 'none',
    outline: (theme: Theme) => `solid 3px ${theme.palette.baseColors.infoBlue.c100}`,
  },
  horizontalScroll: {
    height: 'fit-content',
    width: '100%',
    overflow: 'scroll',
    overflowY: 'hidden',
    display: 'flex',
  },
  applyButton: {
    width: 'fit-content',
    alignSelf: 'flex-end',
    borderRadius: '2em',
    padding: '0.5em 2em',
    textTransform: 'none',
    maxHeight: 'fit-content',
    boxShadow: 'none',
  },
  transparent: { backgroundColor: 'transparent', color: 'black' },
  componentGrid: {
    borderTop: 'solid thin lightgrey',
    display: 'grid',
    paddingTop: '.5rem',
    gridTemplateColumns: {
      xs: '1fr',
      lg: 'repeat(2, 1fr)',
      xl: 'repeat(3, 1fr)',
    },
    gap: '.5rem',
  },
};

type LayoutItemProps = {
  layout: LayoutDefinition;
};

const LayoutItem = ({ layout }: LayoutItemProps) => {
  const { portfolio, selectedPage, applyLayout } = useEditSmartReport();

  if (!portfolio) {
    return null;
  }

  const LayoutThumbnail = layout.Thumbnail as React.ComponentType<{
    sx?: unknown;
    portfolio?: unknown;
    onClick?: () => void;
  }>;
  return (
    <Tooltip title={layout.title} placement='top' arrow>
      <span>
        <LayoutThumbnail
          sx={{
            aspectRatio: '16/9',
            margin: '10px',
            minWidth: '200px',
            height: 'auto',
            border: (theme: Theme) => `solid thin ${theme.palette.baseColors.gray.c100}`,
            cursor: 'pointer',
            ...(selectedPage?.layout === layout.id && {
              border: (theme: Theme) => `solid 3px ${theme.palette.baseColors.infoBlue.c100}`,
            }),
          }}
          portfolio={portfolio}
          onClick={() => applyLayout(layout)}
        />
      </span>
    </Tooltip>
  );
};

type ComponentItemProps = {
  title: string;
  Icon: OverridableComponent<SvgIconTypeMap>;
  components: ComponentDefinition<unknown>[];
};

const ComponentItem = ({ title, Icon, components }: ComponentItemProps) => {
  const { portfolio } = useEditSmartReport();
  const [expanded, setExpanded] = useState(true);

  if (components.length === 0 || !portfolio) {
    return null;
  }

  return (
    <Accordion
      sx={styles.transparent}
      style={{ width: '100%' }}
      square={true}
      expanded={expanded}
      onChange={(_, exp) => setExpanded(exp)}
    >
      <AccordionSummary
        expandIcon={<GridExpandMoreIcon sx={styles.transparent} />}
        aria-controls='panel-layers'
        sx={{ px: '0' }}
      >
        <Box display='flex' alignItems='center'>
          <Icon sx={{ fontSize: 18, marginRight: 1 }} />
          <Typography
            sx={{
              color: (theme: Theme) => theme.palette.text.secondary,
              fontSize: (theme: Theme) => theme.fontSizes.smaller2,
              textTransform: 'lowercase',
            }}
          >
            {title}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={styles.transparent} style={{ padding: 0 }}>
        <Box sx={styles.componentGrid}>
          {components.map((item, idx) => {
            const ThumbnailComponent = getThumbnailComponent(item);

            if (!ThumbnailComponent) {
              return null;
            }

            return (
              <Fragment key={`smart-reports-component-item-components-${item.type}-${idx}`}>
                <DragContainer item={item}>
                  <Box>
                    <ThumbnailComponent portfolio={portfolio} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: (theme: Theme) => theme.fontSizes.smallest,
                      fontWeight: 500,
                      color: (theme: Theme) => theme.palette.text.primary,
                      marginTop: '0.25rem',
                      lineHeight: 1.2,
                      textWrap: 'pretty',
                    }}
                  >
                    {item.name}
                  </Typography>
                </DragContainer>
              </Fragment>
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export const ComponentsList = () => {
  const { report, selectedLayout } = useEditSmartReport();

  const { components: textFieldComponents } = useGetComponents({
    type: SmartReportComponentTypesEnum.TextField,
    reportType: report?.smartReportType?.name,
    layout: selectedLayout,
  });

  const { components: imageComponents } = useGetComponents({
    type: SmartReportComponentTypesEnum.Image,
    reportType: report?.smartReportType?.name,
    layout: selectedLayout,
  });

  const { components: textAreaComponents } = useGetComponents({
    type: SmartReportComponentTypesEnum.TextArea,
    reportType: report?.smartReportType?.name,
    layout: selectedLayout,
  });

  const { components: chartComponents } = useGetComponents({
    type: SmartReportComponentTypesEnum.Chart,
    reportType: report?.smartReportType?.name,
    layout: selectedLayout,
  });

  const { components: tableComponents } = useGetComponents({
    type: SmartReportComponentTypesEnum.Table,
    reportType: report?.smartReportType?.name,
    layout: selectedLayout,
  });

  return (
    <Grid container spacing={2}>
      <Grid size={12} key={`smart-reports-components-text-fields`}>
        <ComponentItem
          title={'Text Fields'}
          Icon={COMPONENT_TYPES_ICONS[SmartReportComponentTypesEnum.TextField]}
          components={textFieldComponents as ComponentDefinition<unknown>[]}
        />
        <ComponentItem
          title={'Text Areas'}
          Icon={COMPONENT_TYPES_ICONS[SmartReportComponentTypesEnum.TextArea]}
          components={textAreaComponents as ComponentDefinition<unknown>[]}
        />
        <ComponentItem
          title={'Images'}
          Icon={COMPONENT_TYPES_ICONS[SmartReportComponentTypesEnum.Image]}
          components={imageComponents as ComponentDefinition<unknown>[]}
        />

        <ComponentItem
          title={'Charts'}
          Icon={COMPONENT_TYPES_ICONS[SmartReportComponentTypesEnum.Chart]}
          components={chartComponents as ComponentDefinition<unknown>[]}
        />
        <ComponentItem
          title={'Tables'}
          Icon={COMPONENT_TYPES_ICONS[SmartReportComponentTypesEnum.Table]}
          components={tableComponents as ComponentDefinition<unknown>[]}
        />
      </Grid>
    </Grid>
  );
};

export const SideBar = () => {
  const { report, entity } = useEditSmartReport();

  const reportTypeName = report?.smartReportType?.name as SmartReportTypeEnum;

  const layouts = SmartReportLayouts.filter(layout => {
    if (!('reportTypes' in layout) || !layout.reportTypes) {
      return true;
    }
    if (!reportTypeName) {
      return false;
    }
    return layout.reportTypes.includes(reportTypeName);
  }) as unknown[] as LayoutDefinition[];

  return (
    <Grid container spacing={2} padding={1}>
      <Grid size={12} display='flex' justifyContent='space-between'>
        <Typography
          sx={{
            fontWeight: 600,
            color: (theme: Theme) => theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontSize: (theme: Theme) => theme.fontSizes.smallest,
          }}
        >
          Entities
        </Typography>
        <Typography
          sx={{
            fontSize: (theme: Theme) => theme.fontSizes.smaller,
            fontWeight: 600,
            color: (theme: Theme) => theme.palette.text.primary,
          }}
        >
          {entity?.entity?.name}
        </Typography>
      </Grid>
      <Grid size={12} display='flex' justifyContent='space-between'>
        <Typography
          sx={{
            fontWeight: 600,
            color: (theme: Theme) => theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.025em',
            fontSize: (theme: Theme) => theme.fontSizes.smallest,
          }}
        >
          Layout
        </Typography>
      </Grid>
      <Grid size={12} display='flex' justifyContent='flex-end' flexDirection='column'>
        <Grid sx={styles.horizontalScroll} size={12} display='flex'>
          {layouts.map((layout, idx) => (
            <LayoutItem key={`smart-reports-layout-item-${layout.id}-${idx}`} layout={layout} />
          ))}
          <Box sx={{ width: 0, height: '100%' }} />
        </Grid>
      </Grid>

      <Grid size={12} display='flex' justifyContent='space-between'>
        <Typography
          sx={{
            fontWeight: 600,
            color: (theme: Theme) => theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.025em',
            fontSize: (theme: Theme) => theme.fontSizes.smallest,
          }}
        >
          Components
        </Typography>
      </Grid>
      <Grid size={12} display='flex' flexDirection='column'>
        <ComponentsList />
      </Grid>
    </Grid>
  );
};
